import { CreditCard, Landmark } from 'lucide-react'
import type { PaymentMethod } from '@/lib/hooks'

type Props = {
  source: PaymentMethod
  imageUrl?: string | null
  size?: 'sm' | 'md' | 'lg'
}

const DEFAULT_COLOR = '#475569'

export function PaymentMethodCard({ source, imageUrl, size = 'md' }: Props) {
  const dimensions = size === 'lg'
    ? 'h-44 w-72'
    : size === 'sm'
      ? 'h-20 w-32'
      : 'h-28 w-44'
  const padding = size === 'sm' ? 'p-2' : size === 'lg' ? 'p-4' : 'p-3'
  const titleSize = size === 'sm' ? 'text-[10px]' : size === 'lg' ? 'text-sm' : 'text-xs'
  const subSize = size === 'sm' ? 'text-[9px]' : size === 'lg' ? 'text-xs' : 'text-[10px]'
  const brandSize = size === 'sm' ? 'text-[10px]' : size === 'lg' ? 'text-base' : 'text-xs'

  const color = source.color ?? DEFAULT_COLOR
  const Icon = source.type === 'credit_card' ? CreditCard : Landmark

  return (
    <div
      className={`relative ${dimensions} ${padding} flex flex-col justify-between overflow-hidden rounded-xl text-white shadow-sm`}
      style={{
        backgroundColor: color,
        backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {imageUrl && <div className="absolute inset-0 bg-black/30" aria-hidden="true" />}
      <div className="relative flex items-start justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="size-4 shrink-0" />
          <p className={`${titleSize} font-medium truncate max-w-[140px]`}>{source.name}</p>
        </div>
      </div>
      <div className="relative flex items-end justify-between">
        <div>
          {source.type === 'credit_card' && source.last4 && (
            <p className={`${subSize} tabular-nums tracking-wider`}>•••• {source.last4}</p>
          )}
          {source.type === 'bank_account' && source.bank_name && (
            <p className={`${subSize} truncate max-w-[160px]`}>{source.bank_name}</p>
          )}
        </div>
        {source.type === 'credit_card' && source.brand && (
          <span className={`${brandSize} font-bold italic`}>{source.brand}</span>
        )}
      </div>
    </div>
  )
}
